import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Star, Copy, Trash2, Power, Sun, Moon, Check, Palette } from 'lucide-react';
import api from '@/services/apiClient';
import { useTheme } from '@/contexts/ThemeContext';
import PageHeader from '@/components/ui/PageHeader';
import Card from '@/components/ui/Card';
import Badge from '@/components/ui/Badge';
import Button from '@/components/ui/Button';
import Spinner from '@/components/ui/Spinner';
import styles from './Themes.module.css';

// Tokens de cor exibidos como swatches (subconjunto relevante).
const SWATCH_KEYS = ['bg', 'surf', 'surf2', 'line', 'hi', 'mid', 'low', 'volt', 'volt2', 'frost', 'ink', 'success', 'warning', 'danger'];

function Swatches({ tokens }) {
  if (!tokens) return <span style={{ color: 'var(--low)', fontSize: 12 }}>—</span>;
  return (
    <div className={styles.swatches}>
      {SWATCH_KEYS.filter((k) => tokens[k]).map((k) => (
        <div key={k} className={styles.swatch} title={`${k}: ${tokens[k]}`}>
          <span className={styles.chip} style={{ background: tokens[k] }} />
          <span className={styles.chipLabel}>{k}</span>
        </div>
      ))}
    </div>
  );
}

export default function AdminThemes() {
  const { t } = useTranslation();
  const { selectTheme } = useTheme();
  const [themes, setThemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [mode, setMode] = useState('dark');
  const [busy, setBusy] = useState(null);
  const [usage, setUsage] = useState(null);

  const load = () => {
    setLoading(true);
    api
      .get('/themes/admin/all')
      .then((res) => {
        const list = (res.data || res).themes || res.data || [];
        setThemes(list);
        setSelected((cur) => list.find((x) => x.id === cur?.id) || list[0] || null);
      })
      .catch(() => setThemes([]))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  // Métricas de uso: quantos usuários estão em cada tema/modo.
  useEffect(() => {
    api
      .get('/themes/admin/usage')
      .then((res) => setUsage(res.data || res))
      .catch(() => setUsage(null));
  }, []);

  const usageOf = (th) => usage?.byTheme?.[th.code] || 0;
  const usagePct = (th) => (usage?.total ? Math.round((usageOf(th) / usage.total) * 100) : 0);

  const act = async (key, fn) => {
    setBusy(key);
    try {
      await fn();
      load();
    } catch (e) {
      alert(e.message);
    } finally {
      setBusy(null);
    }
  };

  const setDefault = (th) => act(th.id + 'def', () => api.post(`/themes/admin/${th.id}/default`));
  const duplicate = (th) => act(th.id + 'dup', () => api.post(`/themes/admin/${th.id}/duplicate`));
  const remove = (th) => {
    if (!confirm(t('themes.confirmDelete', { name: th.name }))) return;
    act(th.id + 'del', () => api.delete(`/themes/admin/${th.id}`));
  };
  const toggleActive = (th) =>
    act(th.id + 'act', () => api.put(`/themes/admin/${th.id}`, { isActive: !th.isActive }));

  const variant = selected?.variants?.[mode] || selected?.variants?.dark || selected?.variants?.light;

  return (
    <>
      <PageHeader kicker="Movez · Admin" title={t('nav.themes')}>
        {usage && (
          <div style={{ display: 'flex', gap: 8 }}>
            <Badge variant="accent">
              <Moon size={11} strokeWidth={2} style={{ marginRight: 4, verticalAlign: -1 }} />
              {t('themes.dark')} · {usage.byMode.dark}
            </Badge>
            <Badge variant="neutral">
              <Sun size={11} strokeWidth={2} style={{ marginRight: 4, verticalAlign: -1 }} />
              {t('themes.light')} · {usage.byMode.light}
            </Badge>
          </div>
        )}
      </PageHeader>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-12)' }}><Spinner /></div>
      ) : (
        <div className={styles.layout}>
          <div className={styles.list}>
            {themes.map((th) => (
              <Card
                key={th.id}
                className={`${styles.themeCard} ${selected?.id === th.id ? styles.themeActive : ''}`}
                onClick={() => setSelected(th)}
                padding="sm"
              >
                <div className={styles.themeHead}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ display: 'inline-flex', color: 'var(--volt)' }}>
                      <Palette size={18} strokeWidth={2} />
                    </span>
                    <div>
                      <div className={styles.themeName}>{th.name}</div>
                      <div className={styles.themeCode}>{th.code}</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {th.isDefault && <Badge variant="accent">{t('themes.default')}</Badge>}
                    <Badge variant={th.isActive ? 'success' : 'neutral'}>{th.isActive ? t('themes.activeLabel') : t('themes.inactiveLabel')}</Badge>
                  </div>
                </div>
                <div className={styles.dots}>
                  {SWATCH_KEYS.slice(0, 8).map((k) => {
                    const v = th.variants?.dark?.[k] || th.variants?.light?.[k];
                    return v ? <span key={k} className={styles.dot} style={{ background: v }} /> : null;
                  })}
                </div>
                {usage && (
                  <div className={styles.usage}>
                    <div className={styles.usageTrack}>
                      <div className={styles.usageFill} style={{ width: `${usagePct(th)}%` }} />
                    </div>
                    <span className={styles.usageLabel}>
                      {t('themes.usageCount', { count: usageOf(th), pct: usagePct(th) })}
                    </span>
                  </div>
                )}
              </Card>
            ))}
          </div>

          <div className={styles.detail}>
            {selected ? (
              <>
                <Card>
                  <div className={styles.detailHead}>
                    <div>
                      <div className="kicker">{selected.code}</div>
                      <h2 className={styles.detailTitle}>{selected.icon} {selected.name}</h2>
                      {selected.description && <p className={styles.desc}>{selected.description}</p>}
                    </div>
                    <div className={styles.modeToggle}>
                      <button className={mode === 'dark' ? styles.modeOn : styles.modeBtn} onClick={() => setMode('dark')}>
                        <Moon size={14} /> {t('themes.dark')}
                      </button>
                      <button className={mode === 'light' ? styles.modeOn : styles.modeBtn} onClick={() => setMode('light')}>
                        <Sun size={14} /> {t('themes.light')}
                      </button>
                    </div>
                  </div>

                  <div className={styles.actions}>
                    {!selected.isDefault && (
                      <Button size="sm" variant="outline" icon={Star} loading={busy === selected.id + 'def'}
                        onClick={() => setDefault(selected)}>{t('themes.setDefault')}</Button>
                    )}
                    <Button size="sm" variant="ghost" icon={Copy} loading={busy === selected.id + 'dup'}
                      onClick={() => duplicate(selected)}>{t('themes.duplicate')}</Button>
                    <Button size="sm" variant="ghost" icon={Power} loading={busy === selected.id + 'act'}
                      onClick={() => toggleActive(selected)}>{selected.isActive ? t('themes.deactivate') : t('themes.activate')}</Button>
                    <Button size="sm" variant="ghost" icon={Check}
                      onClick={() => selectTheme(selected.code)}>{t('themes.preview')}</Button>
                    {!selected.isDefault && (
                      <Button size="sm" variant="danger" icon={Trash2} loading={busy === selected.id + 'del'}
                        onClick={() => remove(selected)} />
                    )}
                  </div>
                </Card>

                <Card style={{ marginTop: 'var(--space-4)' }}>
                  <div className="kicker" style={{ marginBottom: 14 }}>{t('themes.tokens')} · {mode === 'dark' ? t('themes.modeDark') : t('themes.modeLight')}</div>
                  <Swatches tokens={variant} />
                </Card>

                {variant && (
                  <Card style={{ marginTop: 'var(--space-4)' }} padding="none">
                    <div className="kicker" style={{ padding: 'var(--space-4) var(--space-5) 0' }}>{t('themes.livePreview')}</div>
                    <div className={styles.preview} style={{ background: variant.bg, color: variant.hi }}>
                      <div className={styles.previewCard} style={{ background: variant.surf, borderColor: variant.line }}>
                        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: variant.low }}>
                          {selected.name}
                        </span>
                        <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, textTransform: 'uppercase', fontSize: 24, color: variant.hi }}>
                          Movez Flux
                        </div>
                        <p style={{ color: variant.mid, fontSize: 13 }}>{t('themes.previewTagline')}</p>
                        <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
                          <span style={{ background: variant.grad || variant.volt, color: variant.ink, padding: '8px 16px', borderRadius: 10, fontFamily: 'var(--font-display)', fontWeight: 600, textTransform: 'uppercase', fontSize: 13 }}>
                            {t('themes.previewStart')}
                          </span>
                          <span style={{ border: `1px solid ${variant.volt}`, color: variant.volt, padding: '8px 16px', borderRadius: 10, fontFamily: 'var(--font-display)', fontWeight: 600, textTransform: 'uppercase', fontSize: 13 }}>
                            {t('themes.previewExplore')}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Card>
                )}
              </>
            ) : (
              <Card><span style={{ color: 'var(--mid)' }}>{t('themes.selectPrompt')}</span></Card>
            )}
          </div>
        </div>
      )}
    </>
  );
}
